"use client";

import { forwardRef, type ReactNode } from "react";
import { motion, useReducedMotion, type HTMLMotionProps, type Variants } from "framer-motion";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export const staggerContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export const staggerItemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 22,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: EASE_OUT,
    },
  },
};

type FadeInSectionProps = Omit<HTMLMotionProps<"div">, "children"> & {
  children: ReactNode;
  delay?: number;
  y?: number;
  once?: boolean;
  amount?: number;
};

export function FadeInSection({
  children,
  delay = 0,
  y = 24,
  once = true,
  amount = 0.2,
  transition,
  ...props
}: FadeInSectionProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y }}
      whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={
        prefersReducedMotion
          ? undefined
          : {
              duration: 0.6,
              ease: EASE_OUT,
              delay,
              ...transition,
            }
      }
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function FadeIn(props: FadeInSectionProps) {
  return <FadeInSection {...props} />;
}

type StaggerGroupProps = Omit<HTMLMotionProps<"div">, "children"> & {
  children: ReactNode;
};

export function StaggerGroup({ children, ...props }: StaggerGroupProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      variants={prefersReducedMotion ? undefined : staggerContainerVariants}
      initial={prefersReducedMotion ? false : "hidden"}
      whileInView={prefersReducedMotion ? undefined : "visible"}
      viewport={{ once: true, amount: 0.15 }}
      {...props}
    >
      {children}
    </motion.div>
  );
}

type MotionButtonProps = Omit<HTMLMotionProps<"button">, "children"> & {
  children: ReactNode;
  hoverScale?: number;
  tapScale?: number;
};

export const MotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
  function MotionButton(
    {
      children,
      className,
      hoverScale = 1.02,
      tapScale = 0.985,
      ...props
    },
    ref,
  ) {
    const prefersReducedMotion = useReducedMotion();

    return (
      <motion.button
        ref={ref}
        type={props.type ?? "button"}
        whileHover={prefersReducedMotion ? undefined : { scale: hoverScale, y: -1 }}
        whileTap={prefersReducedMotion ? undefined : { scale: tapScale }}
        transition={{
          duration: 0.18,
          ease: EASE_OUT,
        }}
        className={className}
        {...props}
      >
        {children}
      </motion.button>
    );
  },
);
